import React from 'react';
import { motion } from 'framer-motion';
import {
  LuBarcode,
  LuChartColumn,
  LuCreditCard,
  LuPackage,
  LuReceipt,
  LuSmartphone,
  LuStore,
  LuUsers,
} from 'react-icons/lu';

const leftFeatures = [
  { icon: LuCreditCard, label: 'Payments' },
  { icon: LuPackage, label: 'Inventory' },
  { icon: LuReceipt, label: 'Invoices' },
  { icon: LuUsers, label: 'Customers' },
];

const rightFeatures = [
  { icon: LuBarcode, label: 'Barcode' },
  { icon: LuChartColumn, label: 'Reports' },
  { icon: LuSmartphone, label: 'Mobile' },
  { icon: LuStore, label: 'Multi-store' },
];

function FeatureIcon({ icon: Icon, label, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, delay }}
      className="relative z-10 flex flex-col items-center gap-2"
    >
      {/* Outer soft circle + inner branding — only a small gap between them */}
      <div className="relative flex h-[3.65rem] w-[3.65rem] items-center justify-center transition-transform duration-300 hover:scale-105 sm:h-16 sm:w-16">
        <div className="absolute inset-0 rounded-full bg-primary/30" />
        <div className="relative z-10 flex h-[2.85rem] w-[2.85rem] items-center justify-center rounded-full bg-primary text-black shadow-[0_6px_16px_rgba(243,199,21,0.4)] sm:h-12 sm:w-12">
          <Icon size={20} strokeWidth={2.25} />
        </div>
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500 sm:text-[11px]">
        {label}
      </span>
    </motion.div>
  );
}

const connectorPaths = [
  // Smooth fan into center — Y stays ordered so lines never cross
  { id: 'riyo-l1', d: 'M100 68 Q 340 68 545 235', side: 'left' },
  { id: 'riyo-l2', d: 'M100 175 Q 360 175 545 250', side: 'left' },
  { id: 'riyo-l3', d: 'M100 345 Q 360 345 545 270', side: 'left' },
  { id: 'riyo-l4', d: 'M100 452 Q 340 452 545 285', side: 'left' },
  { id: 'riyo-r1', d: 'M1100 68 Q 860 68 655 235', side: 'right' },
  { id: 'riyo-r2', d: 'M1100 175 Q 840 175 655 250', side: 'right' },
  { id: 'riyo-r3', d: 'M1100 345 Q 840 345 655 270', side: 'right' },
  { id: 'riyo-r4', d: 'M1100 452 Q 860 452 655 285', side: 'right' },
];

function HubConnectors() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full lg:block"
      viewBox="0 0 1200 520"
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="riyoLineLeft" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f3c715" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#f3c715" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id="riyoLineRight" x1="100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#f3c715" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#f3c715" stopOpacity="0.95" />
        </linearGradient>
        <filter id="riyoDotGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {connectorPaths.map((path) => (
        <g key={path.id}>
          {/* Base line */}
          <path
            id={path.id}
            d={path.d}
            stroke={path.side === 'left' ? 'url(#riyoLineLeft)' : 'url(#riyoLineRight)'}
            strokeWidth="2"
            strokeLinecap="round"
          />

          {/* Flowing dash pulse along the line */}
          <path
            d={path.d}
            stroke="#f3c715"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="10 18"
            opacity="0.55"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="-56"
              dur="1.6s"
              repeatCount="indefinite"
            />
          </path>

          {/* Traveling dots: icon → center */}
          {[0, 0.55, 1.1].map((begin) => (
            <circle key={`${path.id}-${begin}`} r="3.2" fill="#f3c715" filter="url(#riyoDotGlow)">
              <animateMotion
                dur="2.4s"
                begin={`${begin}s`}
                repeatCount="indefinite"
                path={path.d}
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                keyTimes="0;0.12;0.82;1"
                dur="2.4s"
                begin={`${begin}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
}

const RiyoPos = () => {
  return (
    <section
      id="riyo-pos"
      className="relative overflow-hidden bg-gray-50 py-20 transition-colors duration-300 dark:bg-[#0a0a0a] md:py-28"
    >
      <div className="relative mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="relative z-10 mb-12 text-center md:mb-16"
        >
          <h2 className="text-3xl font-extrabold tracking-tight text-[#111827] dark:text-white sm:text-4xl md:text-5xl">
            Riyo Pos
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[#6b7280] dark:text-gray-400 sm:text-base">
            An all-in-one POS system that connects payments, inventory, invoices, reports, and multi-store tools in one
            simple platform for growing businesses.
          </p>
        </motion.div>

        <div className="relative">
          <HubConnectors />

          <div className="relative z-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-[10rem_minmax(0,1fr)_10rem] lg:gap-4 xl:grid-cols-[11.5rem_minmax(0,1fr)_11.5rem] xl:gap-8">
            <div className="order-2 flex justify-center gap-5 lg:order-1 lg:h-[420px] lg:flex-col lg:items-center lg:justify-between lg:gap-0 lg:py-1">
              {leftFeatures.map((item, index) => (
                <FeatureIcon key={item.label} icon={item.icon} label={item.label} delay={index * 0.05} />
              ))}
            </div>

            <div className="order-1 flex items-center justify-center lg:order-2 lg:h-[420px]">
              <motion.div
                initial={{ scale: 0.88, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="relative flex h-36 w-36 items-center justify-center rounded-full bg-primary shadow-[0_12px_32px_rgba(243,199,21,0.4)] sm:h-40 sm:w-40 md:h-44 md:w-44"
              >
                <img
                  src="/img/riyologo1.png"
                  alt="Riyo Pos"
                  className="h-28 w-28 object-contain sm:h-32 sm:w-32 md:h-36 md:w-36"
                />
              </motion.div>
            </div>

            <div className="order-3 flex justify-center gap-5 lg:h-[420px] lg:flex-col lg:items-center lg:justify-between lg:gap-0 lg:py-1">
              {rightFeatures.map((item, index) => (
                <FeatureIcon key={item.label} icon={item.icon} label={item.label} delay={0.08 + index * 0.05} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RiyoPos;
